import React, { useState, useRef, useEffect } from 'react';
import { Plus, BarChart, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { createProduct, updateProduct, getCategories } from '@/lib/api';
import type { Product, Category } from '@/types/firebase';
import { Textarea } from '@/components/ui/textarea';
import { showNotification, showDuplicateEntryNotification } from '@/lib/generalNotifications';
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { formatCurrency } from '@/lib/utils';
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from "@/components/ui/select";

interface ProductFormData {
  name: string;
  description: string;
  category: string;
  price: string;
  cost: string;
  isTaxable: boolean;
  isService: boolean;
  isSubscription: boolean;
  billingCycle?: 'monthly' | 'quarterly' | 'yearly';
  stockQuantity?: number;
  lowStockAlert?: number;
  sku?: string;
  showCategoryDropdown: boolean;
}

const initialProductFormData: ProductFormData = {
  name: '',
  description: '',
  category: '',
  price: '',
  cost: '',
  isTaxable: false,
  isService: false,
  isSubscription: false,
  stockQuantity: 0,
  lowStockAlert: 5,
  sku: '',
  showCategoryDropdown: false,
};

interface AddEditProductDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  product?: Product | null;
  isEdit?: boolean;
  onAddCategoryOpen: () => void;
  preselectedCategoryId?: string;
  onProductCreated?: (product: Product) => void;
}

export function AddEditProductDialog({
  isOpen,
  onOpenChange,
  companyId,
  product = null,
  isEdit = false,
  onAddCategoryOpen,
  preselectedCategoryId,
  onProductCreated
}: AddEditProductDialogProps) {
  const [formData, setFormData] = useState<ProductFormData>(initialProductFormData);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch categories for the dropdown
  const { data: categories = [] } = useQuery({
    queryKey: ['categories', companyId],
    queryFn: async ({ queryKey }) => {
      const [_, companyId] = queryKey;
      return getCategories(companyId as string);
    },
    enabled: !!companyId && isOpen
  });

  // Initialize form data when product changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      if (product && isEdit) {
        setFormData({
          name: product.name,
          description: product.description,
          category: product.category,
          price: product.price.toString(),
          cost: product.cost ? product.cost.toString() : '',
          isTaxable: product.isTaxable,
          isService: product.isService || false,
          isSubscription: product.isSubscription,
          billingCycle: product.billingCycle,
          stockQuantity: product.stockQuantity,
          lowStockAlert: product.lowStockAlert,
          sku: product.sku || '',
          showCategoryDropdown: false
        });
      } else {
        // For new products, use preselected category if available
        setFormData({
          ...initialProductFormData,
          category: preselectedCategoryId || ''
        });
      }
    }
  }, [product, isEdit, isOpen, preselectedCategoryId]);

  // Update category when a new one is created
  useEffect(() => {
    if (preselectedCategoryId && isOpen && !isEdit) {
      setFormData(prev => ({
        ...prev,
        category: preselectedCategoryId
      }));
    }
  }, [preselectedCategoryId, isOpen, isEdit]);

  // Click outside handler for category dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setFormData(prev => ({ ...prev, showCategoryDropdown: false }));
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: (product: Omit<Product, 'id' | 'createdAt'>) => createProduct(product),
    onSuccess: (createdProduct: Product) => {
      // Call the callback with the created product FIRST, before dialog closes
      if (onProductCreated) {
        onProductCreated(createdProduct);
      }

      queryClient.invalidateQueries({ queryKey: ['products', companyId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      onOpenChange(false);
      setFormData(initialProductFormData);
      showNotification({
        title: "Success",
        message: "Product added successfully",
        type: "success"
      });
    },
    onError: (error: Error) => {
      if (error.message.includes('already exists')) {
        showDuplicateEntryNotification({
          entityType: 'product',
          name: formData.name,
          onEdit: () => {
            const nameInput = document.getElementById('name');
            if (nameInput) {
              nameInput.focus();
            }
          }
        });
      } else {
        showNotification({
          title: "Error",
          message: error.message,
          type: "error"
        });
      }
    }
  });

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: ({ id, product }: { id: string; product: Partial<Product> }) =>
      updateProduct(id, product),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', companyId] });
      onOpenChange(false);
      showNotification({
        title: "Success",
        message: "Product updated successfully",
        type: "success"
      });
    },
    onError: (error: Error) => {
      if (error.message.includes('already exists')) {
        showDuplicateEntryNotification({
          entityType: 'product',
          name: formData.name,
          onEdit: () => {
            const nameInput = document.getElementById('name');
            if (nameInput) {
              nameInput.focus();
            }
          }
        });
      } else {
        showNotification({
          title: "Error",
          message: error.message || "Failed to update product",
          type: "error"
        });
      }
    }
  });

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyId) {
      showNotification({
        title: "Error",
        message: "Company ID not found",
        type: "error"
      });
      return;
    }

    // Validate required fields
    if (!formData.name || !formData.category || !formData.price) {
      showNotification({
        title: "Error",
        message: "Please fill in all required fields",
        type: "error"
      });
      return;
    }

    try {
      // Base product data with required fields
      const baseProductData: any = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        isTaxable: formData.isTaxable,
        isService: formData.isService,
        isSubscription: formData.isSubscription,
        companyId,
        updatedAt: new Date(),
      };

      // Add cost only if provided (avoid undefined)
      if (formData.cost) {
        baseProductData.cost = parseFloat(formData.cost);
      }

      // Add inventory fields only for non-service products
      const inventoryFields = !formData.isService && !formData.isSubscription
        ? {
            stockQuantity: parseInt(formData.stockQuantity?.toString() || '0'),
            lowStockAlert: parseInt(formData.lowStockAlert?.toString() || '5')
          }
        : {};

      // Only add billingCycle if it's a subscription AND has a value (avoid undefined)
      const subscriptionFields = formData.isSubscription && formData.billingCycle
        ? { billingCycle: formData.billingCycle }
        : {};

      // Add SKU only if provided (avoid undefined)
      const skuField = formData.sku ? { sku: formData.sku } : {};

      // Combine all fields (no undefined values)
      const productData = {
        ...baseProductData,
        ...inventoryFields,
        ...subscriptionFields,
        ...skuField
      };

      if (product && isEdit) {
        await updateProductMutation.mutateAsync({
          id: product.id,
          product: productData
        });
      } else {
        await createProductMutation.mutateAsync(productData);
      }

      // Reset form and close dialog only on success
      setFormData(initialProductFormData);
      onOpenChange(false);

    } catch (error) {
      console.error('Product creation error:', error);

      // Check if it's a duplicate name error
      if (error instanceof Error && error.message.includes('already exists')) {
        showDuplicateEntryNotification({
          entityType: 'product',
          name: formData.name,
          onEdit: () => {
            const nameInput = document.getElementById('name');
            if (nameInput) {
              nameInput.focus();
            }
          }
        });
      } else {
        showNotification({
          title: "Error",
          message: error instanceof Error ? error.message : "Failed to create product",
          type: "error"
        });
      }
    }
  };

  // Handle dialog close
  const handleClose = () => {
    onOpenChange(false);
    setFormData(initialProductFormData);
  };

  // Calculate profit and margin from price and cost
  const calculateProfit = React.useMemo(() => {
    const price = parseFloat(formData.price) || 0;
    const cost = parseFloat(formData.cost) || 0;

    const profit = price - cost;
    const margin = price > 0 ? (profit / price) * 100 : 0;

    return {
      profit,
      margin: margin.toFixed(2)
    };
  }, [formData.price, formData.cost]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Product or Service' : 'Add New Product or Service'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter product description"
                    className="h-20"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <div className="relative" ref={categoryDropdownRef}>
                      <Input
                        id="category"
                        value={formData.category ? categories.find(c => c.id === formData.category)?.name || '' : ''}
                        onChange={(e) => {
                          const searchTerm = e.target.value.toLowerCase();
                          const matchingCategory = categories.find(
                            c => c.name.toLowerCase().includes(searchTerm)
                          );
                          setFormData(prev => ({
                            ...prev,
                            category: matchingCategory?.id || '',
                            showCategoryDropdown: true
                          }));
                        }}
                        onFocus={() => setFormData(prev => ({ ...prev, showCategoryDropdown: true }))}
                        placeholder="Search category..."
                        className="w-full"
                        disabled={!!preselectedCategoryId}
                      />
                      {formData.showCategoryDropdown && !preselectedCategoryId && (
                        <div className="absolute z-50 w-full mt-1 bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                          <div className="p-1 space-y-0.5">
                            {categories
                              .filter((category: Category) => {
                                const searchTerm = formData.category ?
                                  categories.find(c => c.id === formData.category)?.name || '' : '';
                                return !searchTerm ||
                                  category.name.toLowerCase().includes(searchTerm.toLowerCase());
                              })
                              .map((category: Category) => (
                                <div
                                  key={category.id}
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      category: category.id,
                                      showCategoryDropdown: false
                                    }));
                                  }}
                                  className="px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-gray-100"
                                >
                                  {category.name}
                                </div>
                              ))
                            }
                            <div className="border-t border-gray-200 mt-1 pt-1">
                              <Button
                                variant="ghost"
                                className="w-full justify-start text-sm font-normal"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, showCategoryDropdown: false }));
                                  onAddCategoryOpen();
                                }}
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add New Category
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price">Price</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cost">Cost</Label>
                    <Input
                      id="cost"
                      name="cost"
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={handleInputChange}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="flex items-center space-x-4 pt-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isTaxable"
                        checked={formData.isTaxable}
                        onCheckedChange={(checked) =>
                          setFormData(prev => ({ ...prev, isTaxable: checked as boolean }))
                        }
                      />
                      <Label htmlFor="isTaxable">Taxable</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isService"
                        checked={formData.isService}
                        onCheckedChange={(checked) =>
                          setFormData(prev => ({ ...prev, isService: checked as boolean }))
                        }
                      />
                      <Label htmlFor="isService">Service</Label>
                    </div>
                  </div>
                </div>

                {/* Hide inventory fields for services */}
                {!formData.isService && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="stockQuantity">Stock Quantity</Label>
                      <Input
                        id="stockQuantity"
                        name="stockQuantity"
                        type="number"
                        value={formData.stockQuantity}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lowStockAlert">Low Stock Alert</Label>
                      <Input
                        id="lowStockAlert"
                        name="lowStockAlert"
                        type="number"
                        value={formData.lowStockAlert}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="sku">SKU (Optional)</Label>
                  <Input
                    id="sku"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    placeholder="Enter SKU"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createProductMutation.isPending || updateProductMutation.isPending}
                >
                  {createProductMutation.isPending || updateProductMutation.isPending
                    ? (isEdit ? 'Updating...' : 'Adding...')
                    : (isEdit ? 'Update Product' : 'Add Product')
                  }
                </Button>
              </div>
            </form>
          </div>

          {/* Profit calculation box */}
          <div className="md:col-span-1">
            <Card className="p-4 h-full">
              <div className="space-y-5">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-blue-500" />
                  Profit Analysis
                </h3>

                <div className="space-y-4">
                  <div className="border-b pb-4">
                    <div className="text-sm text-gray-500 mb-1">Profit per sale</div>
                    <div className="text-2xl font-semibold flex items-center">
                      <DollarSign className="h-5 w-5 text-green-500" />
                      {formatCurrency(calculateProfit.profit)}
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-1">Profit margin</div>
                    <div className="text-2xl font-semibold flex items-center">
                      {calculateProfit.margin}%
                    </div>

                    {/* Margin bar visual */}
                    <div className="mt-2 bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${
                          parseFloat(calculateProfit.margin) < 15
                            ? 'bg-red-500'
                            : parseFloat(calculateProfit.margin) < 30
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(parseFloat(calculateProfit.margin), 100)}%` }}
                      ></div>
                    </div>

                    <div className="mt-2 text-xs">
                      {parseFloat(calculateProfit.margin) < 15 && (
                        <span className="text-red-500">Low margin. Consider adjusting price or reducing costs.</span>
                      )}
                      {parseFloat(calculateProfit.margin) >= 15 && parseFloat(calculateProfit.margin) < 30 && (
                        <span className="text-yellow-500">Average margin. May be room for improvement.</span>
                      )}
                      {parseFloat(calculateProfit.margin) >= 30 && (
                        <span className="text-green-500">Healthy margin. Good profit potential.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}